// The agent skills that carry the design system, loaded from the repository at
// build time so the Design Language area can show them. The files in
// .claude/skills remain the single source; nothing is copied.

const loaders = import.meta.glob(
  [
    '/.claude/skills/priorities-ai-design-system/**/*.md',
    '/.claude/skills/priorities-ai-visual-design/**/*.md',
    '/.claude/skills/elevation-page-building/**/*.md',
    '/.claude/skills/priorities-ai-app-design/**/*.md',
  ],
  { query: '?raw', import: 'default' },
) as Record<string, () => Promise<string>>;

export interface DesignSkill {
  name: string;
  /** One line for the front door. */
  def: string;
  deprecated?: boolean;
}

/** Order is the order they are listed in. */
export const DESIGN_SKILLS: DesignSkill[] = [
  { name: 'priorities-ai-design-system', def: 'Start here. The owner rulings, the two palettes, the four fonts, and which skill to use for which surface.' },
  { name: 'priorities-ai-visual-design', def: 'Diagrams, HTML and SVG: the night canvas and the exhibition.' },
  { name: 'elevation-page-building', def: 'Elevation pages: primitives, dark mode, migration.' },
  { name: 'priorities-ai-app-design', def: 'The v1 look of the app: sage green, rounded cards. A record, not a guide.', deprecated: true },
];

export const SKILL_ROUTE_BASE = '/elevation/priorities-ai-design-language/skills';
export const SKILL_REPO_BASE = 'https://github.com/brightwes/priorities-ai/blob/main/.claude/skills';

const PREFIX = '/.claude/skills/';

/** Files of one skill, SKILL.md first, as paths relative to the skill folder. */
export function skillFiles(skill: string): string[] {
  const own = Object.keys(loaders)
    .filter((p) => p.startsWith(`${PREFIX}${skill}/`))
    .map((p) => p.slice(PREFIX.length + skill.length + 1));
  return own.sort((a, b) => (a === 'SKILL.md' ? -1 : b === 'SKILL.md' ? 1 : a.localeCompare(b)));
}

export function loadSkillFile(skill: string, file: string): Promise<string> | null {
  const loader = loaders[`${PREFIX}${skill}/${file}`];
  return loader ? loader() : null;
}

/** Split the YAML front matter (name, description) off a SKILL.md. */
export function splitFrontMatter(md: string): { description: string | null; body: string } {
  const m = md.match(/^---\n([\s\S]*?)\n---\n?/);
  if (!m) return { description: null, body: md };
  const d = m[1].match(/description:\s*(?:>-?\s*\n)?([\s\S]*)$/);
  const description = d ? d[1].split('\n').map((l) => l.trim()).filter(Boolean).join(' ') : null;
  return { description, body: md.slice(m[0].length) };
}
